<template>
  <div
    v-on:pointerleave="pointerLeave"
    v-on:pointermove="pointerMove"
    class="fixed left-0 top-0 w-screen h-screen flex items-center justify-center"
  >
    <transition
      enter-class="duration-1000 opacity-0 ease-out"
      enter-to-class="duration-1000 opacity-100 ease-out"
      leave-class="duration-500 opacity-100 ease-out"
      leave-to-class="duration-500 opacity-0 ease-out"
    >
      <div class="fixed top-5 md:top-8 opacity-40 w-full px-5 left-0 text-sm text-center">
        <template v-if="others">{{ others }} other fan{{ others > 1 ? 's are' : ' is'}} here. Who do you stand for?</template>
        <template v-else>Open the link in a new tab to try it out!</template>
      </div>
    </transition>

    <div class="grid place-content-center py-5 fixed left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2">
      <div class="flex items-center uppercase justify-center font-bold tracking-wider text-xs xl:text-sm gap-2 lg:gap-3 text-accent">
        <div class="relative">
          <span class="w-2 h-2 rounded-full bg-accent animate-ping block" />
          <span class="w-2 h-2 rounded-full bg-accent absolute top-0" />
        </div>
        <span>Live</span>
      </div>

      <div class="grid items-center mx-auto grid-cols-match gap-4 lg:gap-5 mt-3 px-5 text-xl sm:text-3xl lg:text-4xl xl:text-5xl">
        <div class="flex items-center gap-2 md:gap-3 justify-end">
          <span class="truncate">France</span>
          <picture class="shrink-0">
            <img
              class="w-10 md:w-12 xl:w-12 aspect-square rounded-full"
              alt="Logo | France"
              decoding="sync"
              width="128"
              height="128"
              importance="high"
              loading="eager"
              src="/images/france.jpg"
            />
          </picture>
        </div>

        <p class="flex items-center text-center gap-2">
          <span>0</span>
          <span>:</span>
          <span>0</span>
        </p>

        <div class="flex items-center justify-start gap-2 md:gap-3">
          <picture class="shrink-0">
            <img
              class="w-10 md:w-12 xl:w-12 aspect-square rounded-full" 
              alt="Logo | Australia"
              decoding="sync"
              width="128"
              height="128"
              importance="high"
              loading="eager"
              src="/images/australia.jpg"
            />
          </picture>
          <span class="truncate">Australia</span>
        </div>
      </div>

      <div class="mt-5 lg:mt-7 ease-out duration-700" :class="counterA && counterB ? '' : 'opacity-0' ">
        <div class="flex space-x-2 items-center justify-center text-xs md:text-sm">
          <span class="opacity-40 tabular-nums">{{ Math.round(liverpoolPercentage) }}%</span>
          <div class="relative overflow-hidden rounded w-1/3 mx-auto border border-black/5 h-1">
            <span class="h-1 bg-[white]/30 w-full block"></span>
            <span class="h-1 absolute left-0 top-0 bg-accent ease-in-out-quint duration-1000 w-0 border-r-2 border-black" :style="`width: ${liverpoolPercentage}%`"></span>
          </div>
          <span class="opacity-40 tabular-nums">{{ 100 - Math.round(liverpoolPercentage) }}%</span>
        </div>
        <div class="text-center mt-1 text-sm tabular-nums">{{ counterA + counterB }} encouragement{{ counterA + counterB > 1 ? 's' : '' }}</div>
      </div>
    </div>
    
    <transition
      enter-class="duration-1000 opacity-0 ease-out delay-500"
      enter-to-class="duration-1000 opacity-100 ease-out delay-500"
      leave-class="duration-500 opacity-100 ease-out"
      leave-to-class="duration-500 opacity-0 ease-out"
    >
      <div v-if="encouragements" class="fixed bottom-5 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col md:flex-row gap-2 md:gap-3">
        <button class="relative rounded inline-flex gap-4 justify-between items-center h-10 px-3 text-sm bg-white/10 lg:hover:bg-accent lg:hover:text-black transition-colors ease-out duration-200 touch-none" v-on:click="encourage('numberA')">
          <span class="absolute inset-0 opacity-0 bg-white/10" ref="teamA" />
          <span class="whitespace-nowrap relative">Encourage France</span>
          <span class="opacity-40 tabular-nums relative">{{ counterA.toLocaleString() }}</span>
        </button>
        <button class="relative rounded inline-flex gap-4 justify-between items-center h-10 px-3 text-sm bg-white/10 lg:hover:bg-accent lg:hover:text-black transition-colors ease-out duration-200 touch-none" v-on:click="encourage('numberB')">
          <span class="absolute inset-0 opacity-0 bg-white/10" ref="teamB" />
          <span class="whitespace-nowrap relative">Encourage Australia</span>
          <span class="opacity-40 tabular-nums relative">{{ counterB.toLocaleString() }}</span>
        </button>
      </div>
    </transition>

    <svg
      v-for="cursor in cursors"
      v-bind:key="cursor.connectionId"
      v-bind:style="{
        transform: `translateX(${cursor.x}px) translateY(${cursor.y}px)`,
      }"
      class="cursor"
      width="0.875rem"
      viewBox="0 0 43 58"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2.64546 3.73621L1 2.34568V4.5V51.5V54.2873L2.77193 52.1357L16.4719 35.5H37.5H40.2326L38.1455 33.7362L2.64546 3.73621Z" class="fill-accent" stroke="black" stroke-width="3"/>
    </svg>
  </div>
</template>

<script>
import Vue from "vue";
import { createClient, LiveObject } from "@liveblocks/client";
import JSConfetti from 'js-confetti';
import anime from 'animejs';

let PUBLIC_KEY = "pk_live_FvQmVlcYbmTtYBXDZMWRkp0o";
let roomId = "liveblocks-encourage";
const jsConfetti = new JSConfetti();

const client = createClient({
  publicApiKey: PUBLIC_KEY,
});

const COLORS = ["#DC2626", "#D97706", "#059669", "#7C3AED", "#DB2777"];
const FRANCE_COLORS =  ["🇫🇷", "🥖", "🇫🇷", "🇫🇷"];
const AUSTRALIA_COLORS =  ["🇦🇺", "🦘", "🇦🇺", "🇦🇺"];

export default Vue.extend({
  data: function () {
    return {
      cursor: null,
      cursors: [],
      others: null,
      counterA: null,
      counterB: null,
      encouragements: null
    };
  },
  mounted: async function () {
    const room = client.enter(roomId, { cursor: null });
    this._room = room;
    this._unsubscribe = room.subscribe("my-presence", this.onPresenceChange);
    this._unsubscribeOthers = room.subscribe("others", this.onOthersChange);

    const { root } = await room.getStorage();
    this.encouragements = root.get("encouragements");

    if (this.encouragements == null) {
      this.encouragements = new LiveObject({ numberA: 0, numberB: 0 });
      root.set("encouragements", this.encouragements);
    } else {
      this.counterA = this.encouragements.get('numberA');
      this.counterB = this.encouragements.get('numberB');
    }

    room.subscribe(this.encouragements, () => {
     if (this.counterA < this.encouragements.get('numberA')) {
       jsConfetti.addConfetti({
        emojis: FRANCE_COLORS
      })
     } else if (this.counterB < this.encouragements.get('numberB')) {
      jsConfetti.addConfetti({
        emojis: AUSTRALIA_COLORS
      })
     }

      this.counterA = this.encouragements.get('numberA');
      this.counterB = this.encouragements.get('numberB');
    });
  },
  destroyed: function () {
    this._unsubscribe();
    this._unsubscribeOthers();
    client.leave(roomId);
  },
  computed: {
    liverpoolPercentage () {
      return ((this.counterA * 100) / (this.counterA + this.counterB)).toFixed(2)
    }
  },
  watch: {
    counterA(newVal, oldVal) {
      if (!oldVal) return;

      anime({
        targets: this.$refs.teamA,
        opacity: [0, 1],
        direction: "alternate",
        duration: 300,
        easing: "easeInOutSine"
      })
    },
    counterB(newVal, oldVal) {
      if (!oldVal) return;

      anime({
        targets: this.$refs.teamB,
        opacity: [0, 1],
        direction: "alternate",
        duration: 300,
        easing: "easeInOutSine"
      })
    }
  },
  methods: {
    encourage: function (team) {
      this.encouragements.set(team, this.encouragements.get(team) + 1)
      jsConfetti.addConfetti({
        confettiColors: team === 'numberA' ? LIVERPOOL_COLORS : REAL_COLORS
      })
    },
    pointerMove: function (e) {
      this._room.updatePresence({
        cursor: {
          x: Math.round(e.clientX),
          y: Math.round(e.clientY),
        },
      });
    },
    pointerLeave: function () {
      this._room.updatePresence({
        cursor: null,
      });
    },
    onPresenceChange: function (presence) {
      this.cursor = presence?.cursor ?? null;
    },
    onOthersChange: function (others) {
      this.others = others.count
      this.cursors = others
        .toArray()
        .filter((user) => user.presence?.cursor)
        .map((user) => ({
          x: user.presence.cursor.x,
          y: user.presence.cursor.y,
          color: COLORS[user.connectionId % COLORS.length],
          connectionId: user.connectionId,
        }));
    },
  },
});
</script>

<style>
.cursor {
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.5s cubic-bezier(0.17, 0.93, 0.38, 1);
}
</style>